import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface InfoTooltipProps {
  text: string;
  children?: React.ReactNode;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Function to calculate and update the tooltip's position
  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top, // We use transform to position it above the icon
        left: rect.left + rect.width / 2,
      });
    }
  };

  // Show tooltip and set its initial position
  const handleMouseEnter = () => {
    updatePosition();
    setShowTooltip(true);
  };

  // Hide tooltip
  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // Add a scroll listener to reposition the tooltip if the user scrolls
  useEffect(() => {
    if (showTooltip) {
      // Use capture phase to catch scroll events on any parent element
      window.addEventListener('scroll', updatePosition, true);
    }
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showTooltip]);

  const tooltipContent = showTooltip ? createPortal(
    <div 
        className="fixed w-64 p-3 text-sm text-white bg-gray-900 rounded-lg shadow-2xl z-50 pointer-events-none"
        style={{ 
            top: `${position.top}px`, 
            left: `${position.left}px`, 
            transform: 'translate(-50%, -110%)', // Move tooltip above the icon
        }}
    >
        {text}
        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-gray-900"></div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative inline-flex items-center">
      {children}
      <div 
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="ml-2"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-gray-400 cursor-pointer hover:text-gray-600"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
        {tooltipContent}
      </div>
    </div>
  );
};